'use client'

import { useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { useSprintMetrics } from '@/hooks/use-sprints'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  Activity,
  Layers
} from 'lucide-react'
import {
  ResponsiveContainer,
  LineChart as RechartsLineChart,
  BarChart as RechartsBarChart,
  AreaChart,
  Line,
  Bar,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Cell
} from 'recharts'
import type { BurndownDataPoint, SprintMetrics } from '@/lib/types/db-sprint-metrics'

interface SprintChartsProps {
  sprintId: string
  projectId?: number
}

function ChartCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-5 w-32" />
        </div>
        <Skeleton className="h-3 w-48 mt-1.5" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-[200px] w-full rounded-lg" />
      </CardContent>
    </Card>
  )
}

export function SprintCharts({ sprintId, projectId }: SprintChartsProps) {
  const t = useTranslations('sprintsPage.charts')
  
  // Use centralized hook for metrics fetching
  const { metrics, loading, error: metricsError } = useSprintMetrics(projectId ?? null, sprintId)
  const error = metricsError?.message ?? null

  const burndownChartData = useMemo(() => {
    if (!metrics?.burndown) return []
    return metrics.burndown.map((point, i) => ({
      day: `Day ${i + 1}`,
      remaining: point.isProjected ? null : point.remainingPoints,
      remainingProjected: point.isProjected ? point.remainingPoints : null,
      ideal: Math.round(point.totalPoints - (point.totalPoints * i / (metrics.burndown.length - 1)))
    }))
  }, [metrics])

  const burnupChartData = useMemo(() => {
    if (!metrics?.burndown) return []
    return metrics.burndown.map((point, i) => ({
      day: `Day ${i + 1}`,
      completed: point.isProjected ? null : point.completedPoints,
      completedProjected: point.isProjected ? point.completedPoints : null,
      total: point.totalPoints
    }))
  }, [metrics])

  const velocityData = useMemo(() => {
    if (!metrics?.burndown || metrics.burndown.length === 0) return []
    return metrics.burndown.map((point, i) => {
      const prevCompleted = i > 0 ? metrics.burndown[i - 1].completedPoints : 0
      const dailyVelocity = point.completedPoints - prevCompleted
      return {
        day: `Day ${i + 1}`,
        velocity: point.isProjected ? null : dailyVelocity,
        velocityProjected: point.isProjected ? dailyVelocity : null
      }
    })
  }, [metrics])

  const cumulativeFlowData = useMemo(() => {
    if (!metrics?.burndown) return []
    return metrics.burndown.map((point, i) => ({
      day: `Day ${i + 1}`,
      completed: point.isProjected ? null : point.completedPoints,
      completedProjected: point.isProjected ? point.completedPoints : null,
      inProgress: point.isProjected ? null : (point.inProgressPoints || 0),
      inProgressProjected: point.isProjected ? (point.inProgressPoints || 0) : null,
      remaining: point.remainingPoints
    }))
  }, [metrics])

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-md bg-primary/10">
            <BarChart3 className="h-4 w-4 text-primary" />
          </div>
          <h2 className="text-lg font-semibold tracking-tight">{t('title')}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <ChartCardSkeleton key={i} />
          ))}
        </div>
      </div>
    )
  }

  if (error || !metrics) {
    return (
      <Card className="border-destructive/50 bg-destructive/5">
        <CardContent className="p-6">
          <p className="text-sm text-destructive">
            {t('errorLoading')}: {error || t('unknownError')}
          </p>
        </CardContent>
      </Card>
    )
  }

  const progressPercent = metrics.points.total > 0
    ? Math.round((metrics.points.completed / metrics.points.total) * 100)
    : 0

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2.5">
        <div className="p-1.5 rounded-md bg-primary/10">
          <BarChart3 className="h-4 w-4 text-primary" />
        </div>
        <h2 className="text-lg font-semibold tracking-tight">{t('title')}</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Burndown Chart - Line chart */}
        <Card className="overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <TrendingDown className="h-4 w-4" />
                  {t('burndown')}
                </CardTitle>
                <CardDescription className="text-xs mt-1">{t('burndownDesc')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pb-4">
            {burndownChartData.length > 0 ? (
              <div className="space-y-4">
                <ResponsiveContainer width="100%" height={200}>
                  <RechartsLineChart data={burndownChartData}>
                    <XAxis
                      dataKey="day"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      dy={8}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      width={32}
                    />
                    <Tooltip
                      cursor={{ stroke: 'hsl(var(--border))', strokeWidth: 1, strokeDasharray: '3 3' }}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px',
                        padding: '6px 10px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        fontSize: '12px'
                      }}
                      labelStyle={{
                        color: 'hsl(var(--popover-foreground))',
                        fontWeight: 600,
                        marginBottom: '4px'
                      }}
                      itemStyle={{
                        color: 'hsl(var(--popover-foreground))',
                        padding: '2px 0'
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="ideal"
                      stroke="hsl(var(--muted-foreground))"
                      strokeDasharray="4 4"
                      strokeWidth={1.5}
                      dot={false}
                      name={t('ideal')}
                    />
                    <Line
                      type="monotone"
                      dataKey="remaining"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2.5}
                      dot={{ fill: 'hsl(var(--primary))', r: 3 }}
                      name={t('remaining')}
                      connectNulls
                    />
                    <Line
                      type="monotone"
                      dataKey="remainingProjected"
                      stroke="hsl(var(--muted-foreground))"
                      strokeWidth={2}
                      strokeOpacity={0.4}
                      dot={{ fill: 'hsl(var(--muted-foreground))', r: 2, opacity: 0.4 }}
                      name={t('projected')}
                      connectNulls
                    />
                  </RechartsLineChart>
                </ResponsiveContainer>

                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="bg-muted/40 rounded-md p-2.5 text-center">
                    <div className="font-semibold text-sm">{metrics.burndown[0]?.totalPoints || 0}</div>
                    <div className="text-muted-foreground text-[11px] mt-0.5">{t('total')}</div>
                  </div>
                  <div className="bg-muted/40 rounded-md p-2.5 text-center">
                    <div className="font-semibold text-sm text-primary">{metrics.points.completed}</div>
                    <div className="text-muted-foreground text-[11px] mt-0.5">{t('done')}</div>
                  </div>
                  <div className="bg-muted/40 rounded-md p-2.5 text-center">
                    <div className="font-semibold text-sm">{metrics.points.total - metrics.points.completed}</div>
                    <div className="text-muted-foreground text-[11px] mt-0.5">{t('left')}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-[200px] border-2 border-dashed border-border rounded-lg flex items-center justify-center">
                <p className="text-sm text-muted-foreground">{t('noData')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Burnup Chart - Step line chart */}
        <Card className="overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <TrendingUp className="h-4 w-4" />
                  {t('burnup')}
                </CardTitle>
                <CardDescription className="text-xs mt-1">{t('burnupDesc')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pb-4">
            {burnupChartData.length > 0 ? (
              <div className="space-y-4">
                <ResponsiveContainer width="100%" height={200}>
                  <RechartsLineChart data={burnupChartData}>
                    <XAxis
                      dataKey="day"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      dy={8}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      width={32}
                    />
                    <Tooltip
                      cursor={{ stroke: 'hsl(var(--border))', strokeWidth: 1, strokeDasharray: '3 3' }}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--popover))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px',
                        padding: '6px 10px',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        fontSize: '12px'
                      }}
                      labelStyle={{
                        color: 'hsl(var(--popover-foreground))',
                        fontWeight: 600,
                        marginBottom: '4px'
                      }}
                      itemStyle={{
                        color: 'hsl(var(--popover-foreground))',
                        padding: '2px 0'
                      }}
                    />
                    <Line
                      type="stepAfter"
                      dataKey="completed"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2.5}
                      dot={{ fill: 'hsl(var(--primary))', r: 4, strokeWidth: 2, stroke: 'hsl(var(--background))' }}
                      activeDot={{ r: 6, strokeWidth: 2 }}
                      name={t('completed')}
                      connectNulls
                    />
                    <Line
                      type="stepAfter"
                      dataKey="completedProjected"
                      stroke="hsl(var(--muted-foreground))"
                      strokeWidth={2}
                      strokeOpacity={0.4}
                      dot={{ fill: 'hsl(var(--muted-foreground))', r: 3, opacity: 0.4 }}
                      name={t('projected')}
                      connectNulls
                    />
                  </RechartsLineChart>
                </ResponsiveContainer>

                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-muted/40 rounded-md p-2.5 text-center">
                    <div className="font-semibold text-sm text-primary">{progressPercent}%</div>
                    <div className="text-muted-foreground text-[11px] mt-0.5">{t('progress')}</div>
                  </div>
                  <div className="bg-muted/40 rounded-md p-2.5 text-center">
                    <div className="font-semibold text-sm">{burnupChartData.length}</div>
                    <div className="text-muted-foreground text-[11px] mt-0.5">{t('days')}</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-[200px] border-2 border-dashed border-border rounded-lg flex items-center justify-center">
                <p className="text-sm text-muted-foreground">{t('noData')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Velocity Trend - Bar chart */}
        <Card className="overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Activity className="h-4 w-4" />
                  {t('velocity')}
                </CardTitle>
                <CardDescription className="text-xs mt-1">{t('velocityDesc')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="space-y-4">
              <ResponsiveContainer width="100%" height={200}>
                <RechartsBarChart data={velocityData}>
                  <XAxis
                    dataKey="day"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    dy={8}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    width={32}
                  />
                  <Tooltip
                    cursor={{ fill: 'hsl(var(--muted) / 0.1)' }}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                      fontSize: '12px'
                    }}
                    labelStyle={{
                      color: 'hsl(var(--popover-foreground))',
                      fontWeight: 600,
                      marginBottom: '4px'
                    }}
                    itemStyle={{
                      color: 'hsl(var(--popover-foreground))',
                      padding: '2px 0'
                    }}
                  />
                  <Bar dataKey="velocity" radius={[4, 4, 0, 0]} fill="hsl(var(--primary))" />
                  <Bar dataKey="velocityProjected" radius={[4, 4, 0, 0]} fill="hsl(var(--muted-foreground))" fillOpacity={0.3} />
                </RechartsBarChart>
              </ResponsiveContainer>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-muted/40 rounded-md p-2.5 text-center">
                  <div className="font-semibold text-sm text-primary">{metrics.issues.completed}</div>
                  <div className="text-muted-foreground text-[11px] mt-0.5">{t('completed')}</div>
                </div>
                <div className="bg-muted/40 rounded-md p-2.5 text-center">
                  <div className="font-semibold text-sm text-muted-foreground">{metrics.issues.inProgress}</div>
                  <div className="text-muted-foreground text-[11px] mt-0.5">{t('inProgress')}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cumulative Flow - Line chart */}
        <Card className="overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Layers className="h-4 w-4" />
                  {t('cumulativeFlow')}
                </CardTitle>
                <CardDescription className="text-xs mt-1">{t('cumulativeFlowDesc')}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pb-4">
            <div className="space-y-4">
              <ResponsiveContainer width="100%" height={200}>
                <RechartsLineChart data={cumulativeFlowData}>
                  <XAxis
                    dataKey="day"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    interval="preserveStartEnd"
                    dy={8}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                    width={32}
                  />
                  <Tooltip
                    cursor={{ stroke: 'hsl(var(--border))', strokeWidth: 1, strokeDasharray: '3 3' }}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px',
                      padding: '6px 10px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                      fontSize: '12px'
                    }}
                    labelStyle={{
                      color: 'hsl(var(--popover-foreground))',
                      fontWeight: 600,
                      marginBottom: '4px'
                    }}
                    itemStyle={{
                      color: 'hsl(var(--popover-foreground))',
                      padding: '2px 0'
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="completed"
                    stroke="hsl(var(--primary))"
                    strokeWidth={2.5}
                    dot={{ fill: 'hsl(var(--primary))', r: 3 }}
                    name={t('completed')}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="completedProjected"
                    stroke="hsl(var(--muted-foreground))"
                    strokeWidth={2}
                    strokeOpacity={0.4}
                    dot={{ fill: 'hsl(var(--muted-foreground))', r: 2, opacity: 0.4 }}
                    name={t('projected')}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="inProgress"
                    stroke="hsl(var(--muted-foreground))"
                    strokeWidth={2.5}
                    dot={{ fill: 'hsl(var(--muted-foreground))', r: 3 }}
                    name={t('inProgress')}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="inProgressProjected"
                    stroke="hsl(var(--muted-foreground))"
                    strokeWidth={2}
                    strokeOpacity={0.3}
                    dot={{ fill: 'hsl(var(--muted-foreground))', r: 2, opacity: 0.3 }}
                    name={t('projected')}
                    connectNulls
                  />
                </RechartsLineChart>
              </ResponsiveContainer>

              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-muted/40 rounded-md p-2.5 text-center">
                  <div className="font-semibold text-sm text-primary">{metrics.issues.completed}</div>
                  <div className="text-muted-foreground text-[11px] mt-0.5">{t('done')}</div>
                </div>
                <div className="bg-muted/40 rounded-md p-2.5 text-center">
                  <div className="font-semibold text-sm text-muted-foreground">{metrics.issues.inProgress}</div>
                  <div className="text-muted-foreground text-[11px] mt-0.5">{t('inProgress')}</div>
                </div>
                <div className="bg-muted/40 rounded-md p-2.5 text-center">
                  <div className="font-semibold text-sm">{metrics.issues.total}</div>
                  <div className="text-muted-foreground text-[11px] mt-0.5">{t('total')}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
